import { create as createApi } from "apisauce";
import axios, { AxiosProgressEvent, AxiosResponse } from "axios";
import { BASE_URL } from "src/app.config";
import { CONSTANTS } from "src/constants";
import { COMMON_CONSTANTS } from "src/constants/commonConstants";
import { URL_CONSTANTS } from "src/constants/url.constants";
import {
  AccessTokenResponse,
  FailedQueueItem,
  OriginalRequestConfig,
} from "src/models/service.models";

import commonService from "../common.service";
import { getRefreshToken } from "../user.service";

import API_URL from "./api.config";

const Create = (baseURL: string | undefined = BASE_URL) => {
  const axiosInstance = axios.create({
    baseURL,
    timeout: CONSTANTS.API.TIMEOUT,
  });

  const api = createApi({
    axiosInstance: axiosInstance as unknown as Parameters<
      typeof createApi
    >[0]["axiosInstance"],
    baseURL,
    timeout: CONSTANTS.API.TIMEOUT,
  });

  let failedQueue: FailedQueueItem[] = [];
  let isRefreshing = COMMON_CONSTANTS.DEFAULT_ACTION_STATE;

  api.axiosInstance.interceptors.response.use(
    response => response,
    async error => {
      const originalRequest = error.config as OriginalRequestConfig;
      if (originalRequest.url === API_URL.USER.ADMIN_LOGIN) {
        processQueue(error.message, null);
        return Promise.reject(error);
      }
      if (originalRequest.url === API_URL.AUTH.REFRESH) {
        processQueue(error.message, null);
        commonService.clearLocalStorage();
        window.location.href = URL_CONSTANTS.LOGIN;
        return Promise.reject(error);
      }

      if (
        error.response?.status === CONSTANTS.HTTP_STATUS.UNAUTHORIZED &&
        !originalRequest._retry
      ) {
        return handleUnauthorizedError(originalRequest);
      }

      return Promise.reject(error);
    }
  );

  const addRequestToQueue = (
    originalRequest: OriginalRequestConfig
  ): Promise<AxiosResponse> => {
    return new Promise<string | null>((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    }).then(token => {
      if (token && originalRequest.headers) {
        originalRequest.headers[CONSTANTS.API.AUTH] =
          CONSTANTS.API.BEARER + token;
      }
      return api.axiosInstance(
        originalRequest as unknown as Parameters<typeof api.axiosInstance>[0]
      ) as unknown as Promise<AxiosResponse>;
    });
  };
  const fileUploadApi = async (
    url: string,
    body?: FormData,
    onUploadProgress?: (progressEvent: AxiosProgressEvent) => void,
    customHeader?: { [key: string]: string }
  ) => {
    if (customHeader) api.setHeaders(customHeader);
    const response = await api.post(url, body, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress: onUploadProgress,
    });
    return response;
  };

  const get = async <T, U = T>(
    url: string,
    params?: { [key: string]: Array<number> | string | number | undefined },
    customHeader?: { [key: string]: string }
  ) => {
    if (customHeader) api.setHeaders(customHeader);
    else await setHeader();
    return api.get<T, U>(url, params);
  };
  const getBlobFile = async (
    url: string,
    params?: { [key: string]: Array<number> | string | number }
  ) => {
    await setHeader();
    return api.get(url, params, {
      headers: {
        "Content-Type": "application/octet-stream",
      },
      responseType: "blob",
    });
  };
  const postBlobFile = async (
    url: string,
    params?: { [key: string]: Array<number> | string | number }
  ) => {
    await setHeader();
    return api.post(url, params, {
      headers: {
        "Content-Type": "application/octet-stream",
      },
      responseType: "blob",
    });
  };
  const handleUnauthorizedError = async (
    originalRequest: OriginalRequestConfig
  ): Promise<AxiosResponse> => {
    if (isRefreshing) {
      return addRequestToQueue(originalRequest);
    }

    originalRequest._retry = CONSTANTS.COMMON_CONSTANTS.YES;
    isRefreshing = CONSTANTS.COMMON_CONSTANTS.YES;
    const newToken = await refreshAccessToken();
    isRefreshing = COMMON_CONSTANTS.DEFAULT_ACTION_STATE;

    if (newToken && originalRequest.headers) {
      originalRequest.headers[CONSTANTS.API.AUTH] =
        CONSTANTS.API.BEARER + newToken.token;
      return api.axiosInstance(
        originalRequest as unknown as Parameters<typeof api.axiosInstance>[0]
      ) as unknown as Promise<AxiosResponse>;
    }

    return Promise.reject(new Error());
  };

  const patch = async <T>(
    url: string,
    body?: T,
    customHeader?: { [key: string]: string }
  ) => {
    if (customHeader) api.setHeaders(customHeader);
    else await setHeader();
    return api.patch(url, body);
  };

  const post = async <T>(url: string, body?: T) => {
    if (url?.includes(API_URL.USER.ADMIN_LOGIN)) await setHeader();
    return api.post(url, body);
  };

  const put = async <T>(
    url: string,
    body?: T,
    customHeader?: { [key: string]: string }
  ) => {
    if (customHeader) api.setHeaders(customHeader);
    else await setHeader();
    return api.put(url, body);
  };

  const processQueue = (error: string | null, token: string | null = null) => {
    failedQueue.forEach(prom => {
      error ? prom.reject(error) : prom.resolve(token);
    });
    failedQueue = [];
  };

  const refreshAccessToken = async (): Promise<AccessTokenResponse | null> => {
    const refreshToken = commonService.getLocalStorage(
      CONSTANTS.LOCAL_STORAGE.REFRESH_TOKEN
    );
    if (!refreshToken) return null;

    try {
      const response = await getRefreshToken(refreshToken);
      if (!response.data) return null;

      const newToken = response.data as AccessTokenResponse;
      commonService.setLocalStorage({
        ACCESS_TOKEN: newToken.token,
        REFRESH_TOKEN: newToken.refresh_token,
      });
      api.axiosInstance.defaults.headers.common[CONSTANTS.API.AUTH] =
        CONSTANTS.API.BEARER + newToken.token;
      processQueue(null, newToken.token);
      return newToken;
    } catch (error) {
      processQueue(error as string, null);
      commonService.clearLocalStorage();
      window.location.href = URL_CONSTANTS.LOGIN;
      return null;
    }
  };

  const remove = async (url: string, body?: { [key: string]: string }) => {
    await setHeader();
    return api.delete(url, body);
  };

  const removeWithBody = async (
    url: string,
    body?: { [key: string]: string | boolean }
  ) => {
    await setHeader();
    return api.delete(url, {}, { data: body });
  };

  const setHeader = async () => {
    const apiHeader = await commonService.getApiHeader();
    api.setHeaders(apiHeader);
  };

  return {
    fileUploadApi,
    get,
    patch,
    removeWithBody,
    getBlobFile,
    postBlobFile,
    post,
    remove,
    put,
    api,
  };
};

export default {
  Create,
};
